import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Printer, Save, FileText, Settings, Sparkles,
  LayoutTemplate, ScanLine, CheckCircle2, AlertCircle, RefreshCcw,
  CloudUpload, Trash2, MessageSquare, Ban, Eye, GripVertical
} from 'lucide-react';
import { DEFAULT_PRICE_LIST_PRINT_CONFIG } from '../utils/priceListExport';

const fmtMoney2 = (value) => Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// --- DEFINICIÓN DE OPCIONES DISPONIBLES (Metadata) ---
const OPTIONS_META = [
  { k: 'showLogo', l: 'Mostrar Logo' },
  { k: 'showClientInfo', l: 'Datos Cliente' },
  { k: 'showTaxBreakdown', l: 'Tabla IVA' },
  { k: 'showDiscounts', l: 'Desc. / Recargos' },
  { k: 'showQr', l: 'QR AFIP' },
  { k: 'showMonotributoLegend', l: 'Leyenda Monotributista' },
  { k: 'showTaxPerRate', l: 'Discriminar por Alícuota' },
  { k: 'showItemTax', l: 'IVA por Item' },
  { k: 'showPaymentMethod', l: 'Método de Pago' },
  // Lista de precios
  { k: 'showListName', l: 'Nombre de la lista' },
  { k: 'showListDate', l: 'Fecha' },
  { k: 'showBrand', l: 'Marca del producto' },
  { k: 'showBarcode', l: 'Código de barras' }
];

// Generamos el orden por defecto basado en la metadata (sin las opciones
// propias de la lista de precios).
const PRICE_LIST_ONLY_KEYS = ['showListName', 'showListDate', 'showBrand', 'showBarcode'];
const DEFAULT_ORDER = OPTIONS_META.map(o => o.k).filter(k => !PRICE_LIST_ONLY_KEYS.includes(k));
const PEDIDO_SUGERIDO_FIELDS_ORDER = ['showLogo'];

// --- CONFIGURACIÓN POR DEFECTO (FALLBACK) ---
const DEFAULT_DOC_CONFIGS = {
  factura: {
    headerText: 'Empresa Demo S.A.', subHeaderText: 'IVA Responsable Inscripto', footerText: 'Gracias por su compra.',
    showLogo: true, logoBase64: null, printerWidth: '80mm', 
    showClientInfo: true, showTaxBreakdown: true, showDiscounts: true, showQr: true, 
    showMonotributoLegend: false, showTaxPerRate: true, showItemTax: false,
    showPaymentMethod: true,
    voucherLetter: 'A',
    printAction: 'ask',
    fieldsOrder: DEFAULT_ORDER
  },
  nota_credito: {
    headerText: 'Empresa Demo S.A.', subHeaderText: 'IVA Responsable Inscripto', footerText: 'El crédito ha sido generado en su cuenta.',
    showLogo: true, logoBase64: null, printerWidth: '80mm', 
    showClientInfo: true, showTaxBreakdown: true, showDiscounts: false, showQr: true, 
    showMonotributoLegend: false, showTaxPerRate: true, showItemTax: false,
    showPaymentMethod: true,
    voucherLetter: 'A',
    printAction: 'ask',
    fieldsOrder: DEFAULT_ORDER
  },
  presupuesto: {
    headerText: 'Empresa Demo S.A.', subHeaderText: 'PRESUPUESTO - NO VÁLIDO COMO FACTURA', footerText: 'Válido por 15 días.',
    showLogo: true, logoBase64: null, printerWidth: 'a4', 
    showClientInfo: true, showTaxBreakdown: false, showDiscounts: true, showQr: false, 
    showMonotributoLegend: false, showTaxPerRate: false, showItemTax: false,
    showPaymentMethod: true,
    voucherLetter: 'X',
    printAction: 'ask',
    fieldsOrder: DEFAULT_ORDER
  },
  recibo: {
    headerText: 'Empresa Demo S.A.', subHeaderText: 'RECIBO DE COBRO', footerText: 'Documento de uso interno.',
    showLogo: true, logoBase64: null, printerWidth: '80mm', 
    showClientInfo: true, showTaxBreakdown: false, showDiscounts: false, showQr: false, 
    showMonotributoLegend: false, showTaxPerRate: false, showItemTax: false,
    showPaymentMethod: true,
    voucherLetter: 'X',
    printAction: 'ask',
    fieldsOrder: DEFAULT_ORDER
  },
  pago: {
    headerText: 'Empresa Demo S.A.', subHeaderText: 'ORDEN DE PAGO', footerText: 'Firma Conforme: .................',
    showLogo: true, logoBase64: null, printerWidth: 'a4', 
    showClientInfo: true, showTaxBreakdown: false, showDiscounts: false, showQr: false, 
    showMonotributoLegend: false, showTaxPerRate: false, showItemTax: false,
    showPaymentMethod: true,
    voucherLetter: 'X',
    printAction: 'ask',
    fieldsOrder: DEFAULT_ORDER
  },
  ticket_venta: {
    headerText: 'Empresa Demo', subHeaderText: 'TICKET NO FISCAL', footerText: 'Conserve este ticket.',
    showLogo: true, logoBase64: null, printerWidth: '58mm', 
    showClientInfo: false, showTaxBreakdown: false, showDiscounts: true, showQr: false, 
    showMonotributoLegend: false, showTaxPerRate: false, showItemTax: false,
    showPaymentMethod: true,
    voucherLetter: 'X',
    printAction: 'ask',
    fieldsOrder: DEFAULT_ORDER
  },
  arqueo: {
    headerText: 'Arqueo de Caja', subHeaderText: 'Control Interno', footerText: 'Firma Cajero: .................',
    showLogo: false, logoBase64: null, printerWidth: '80mm', 
    showClientInfo: false, showTaxBreakdown: false, showDiscounts: false, showQr: false, 
    showMonotributoLegend: false, showTaxPerRate: false, showItemTax: false,
    showPaymentMethod: true,
    voucherLetter: 'X',
    printAction: 'ask',
    fieldsOrder: DEFAULT_ORDER
  },
  pedido_sugerido: {
    headerText: 'Pedido sugerido', subHeaderText: 'Reposicion automatica segun stock minimo', footerText: 'Reporte interno generado desde Impulza.',
    showLogo: true, logoBase64: null, printerWidth: 'a4',
    showClientInfo: false, showTaxBreakdown: false, showDiscounts: false, showQr: false,
    showMonotributoLegend: false, showTaxPerRate: false, showItemTax: false,
    showPaymentMethod: false,
    voucherLetter: 'X',
    printAction: 'ask',
    fieldsOrder: PEDIDO_SUGERIDO_FIELDS_ORDER
  },
  // PDF que se imprime / manda por WhatsApp desde Listas de precios.
  lista_precios: {
    ...DEFAULT_PRICE_LIST_PRINT_CONFIG,
    showClientInfo: false, showTaxBreakdown: false, showDiscounts: false, showQr: false,
    showMonotributoLegend: false, showTaxPerRate: false, showItemTax: false,
    showPaymentMethod: false,
    voucherLetter: 'X',
    printAction: 'ask',
    fieldsOrder: [...DEFAULT_PRICE_LIST_PRINT_CONFIG.fieldsOrder]
  }
};

const cloneDocConfigs = () =>
  Object.fromEntries(
    Object.entries(DEFAULT_DOC_CONFIGS).map(([key, config]) => [
      key,
      {
        ...config,
        fieldsOrder: Array.isArray(config.fieldsOrder) ? [...config.fieldsOrder] : [...DEFAULT_ORDER]
      }
    ])
  );

const DOC_TYPES = [
  { id: 'factura', label: 'Facturas (Fiscal)' },
  { id: 'nota_credito', label: 'Notas de Crédito' },
  { id: 'ticket_venta', label: 'Ticket Comandera (Interno)' },
  { id: 'presupuesto', label: 'Presupuestos' },
  { id: 'recibo', label: 'Recibos de Cobro' },
  { id: 'pago', label: 'Órdenes de Pago' },
  { id: 'pedido_sugerido', label: 'Pedido sugerido' },
  { id: 'lista_precios', label: 'Lista de precios (PDF / WhatsApp)' },
  { id: 'arqueo', label: 'Cierre de Caja' },
];

export default function ConfigReportes() {
  const { profile, organization, refreshOrganization } = useAuth();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentDocType, setCurrentDocType] = useState('factura');
  const [docConfigs, setDocConfigs] = useState(() => cloneDocConfigs());
  const [empresaData, setEmpresaData] = useState(null);
  const [posDefaultAction, setPosDefaultAction] = useState('popout'); // 'popout', 'none', 'ticket', 'factura'
  const [silentPrint, setSilentPrint] = useState(false); // Saltar el modal de previsualización y mandar directo a imprimir

  // Refs para Drag & Drop
  const dragItem = useRef();
  const dragOverItem = useRef();

  // Carga inicial — los datos vienen del AuthContext (un fetch por sesión).
  useEffect(() => {
    if (!organization) return;
    setLoading(true);
    try {
      const data = organization;
      if (data?.print_config) {
        setPosDefaultAction(data.print_config.posDefaultAction || (data.print_config.showPostSaleActions === false ? 'none' : 'popout'));
        setSilentPrint(Boolean(data.print_config.silentPrint));
        const merged = cloneDocConfigs();
        Object.keys(data.print_config).forEach(key => {
          if (DEFAULT_DOC_CONFIGS[key]) {
            const config = data.print_config[key] || {};
            if (!config.fieldsOrder) {
              config.fieldsOrder = [...(DEFAULT_DOC_CONFIGS[key].fieldsOrder || DEFAULT_ORDER)];
            } else {
              config.fieldsOrder = [...config.fieldsOrder];
            }
            merged[key] = { ...DEFAULT_DOC_CONFIGS[key], ...config };
          }
        });
        // Lista de precios sin configurar: arranca con el nombre de la empresa.
        if (!merged.lista_precios.headerText) merged.lista_precios.headerText = data?.name || '';
        setDocConfigs(merged);
      } else {
        const prefill = cloneDocConfigs();
        Object.keys(prefill).forEach(key => {
          prefill[key].headerText = data?.name || 'Mi Empresa';
          prefill[key].subHeaderText = (data?.full_address || '') + '\n' + (data?.tax_condition || '');
        });
        setDocConfigs(prefill);
      }
      setEmpresaData(data);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  }, [organization]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // print_config viene del AuthContext (cargado una vez por sesión)
      const currentFullConfig = organization?.print_config || {};

      const fullConfigToSave = {
        ...currentFullConfig,
        ...docConfigs,
        posDefaultAction,
        silentPrint
      };
      
      const { error: orgError } = await supabase
        .from('organizations')
        .update({ print_config: fullConfigToSave })
        .eq('id', profile.organization_id);

      if (orgError) throw orgError;

      const facturaConfig = docConfigs['factura'];
      const legacyData = {
        organization_id: profile.organization_id,
        page_format: facturaConfig.printerWidth === 'a4' ? 'Hoja A4 (Estándar)' : 'Ticket 80mm',
        show_company_address: true,
        show_company_cuit: true,
        footer_message: facturaConfig.footerText
      };

      await supabase.from('printed_reports_config').upsert(legacyData, { onConflict: 'organization_id' });
      await refreshOrganization?.();
      toast.success('Configuración guardada correctamente');
    } catch (err) {
      console.error(err);
      toast.error('Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const updateCurrentConfig = (key, value) => {
    setDocConfigs(prev => ({
      ...prev,
      [currentDocType]: { ...prev[currentDocType], [key]: value }
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 150000) { 
      return toast.error("La imagen es muy pesada. Máximo 150KB.");
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      updateCurrentConfig('logoBase64', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDragStart = (e, position) => {
    dragItem.current = position;
    e.target.style.opacity = '0.5';
  };

  const handleDragEnter = (e, position) => {
    dragOverItem.current = position;
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    const listCopy = [...ticketConfig.fieldsOrder];
    const dragItemContent = listCopy[dragItem.current];
    listCopy.splice(dragItem.current, 1);
    listCopy.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = null;
    dragOverItem.current = null;
    updateCurrentConfig('fieldsOrder', listCopy);
  };

  const ticketConfig = docConfigs[currentDocType] || DEFAULT_DOC_CONFIGS.factura;
  const isPedidoSugerido = currentDocType === 'pedido_sugerido';
  const isListaPrecios = currentDocType === 'lista_precios';

  // --- RENDERIZADO DE PREVIEW ---
  const renderPreviewContent = () => {
    if (currentDocType === 'pedido_sugerido') {
      return (
        <div className="w-full space-y-2 mb-2">
          <div className="grid grid-cols-3 gap-2 text-[8px]">
            <div className="rounded border border-slate-200 bg-slate-50 p-2">
              <div className="text-slate-400 uppercase font-bold">Productos</div>
              <div className="text-[11px] font-black text-slate-800 mt-1">18</div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-2">
              <div className="text-slate-400 uppercase font-bold">Unidades</div>
              <div className="text-[11px] font-black text-emerald-600 mt-1">124</div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-2">
              <div className="text-slate-400 uppercase font-bold">Inversion</div>
              <div className="text-[11px] font-black text-amber-600 mt-1">$245.320</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-1 text-[8px]">
            <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">Sucursal: Casa central</span>
            <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">Proveedor: Distribuidora Sur</span>
          </div>

          <div className="border border-slate-200 rounded overflow-hidden">
            <div className="grid grid-cols-[2fr_1fr_1.3fr_0.8fr] bg-[#0B2A36] text-white text-[7px] font-black uppercase tracking-widest">
              <div className="px-2 py-1.5">Producto</div>
              <div className="px-2 py-1.5">Marca</div>
              <div className="px-2 py-1.5">Ult. proveedor</div>
              <div className="px-2 py-1.5 text-right">Reponer</div>
            </div>
            {[
              { producto: 'Harina 000', marca: 'Molino Azul', proveedor: 'Distribuidora Sur', qty: '40' },
              { producto: 'Levadura seca', marca: 'Nova', proveedor: 'Panificados SRL', qty: '12' },
              { producto: 'Azucar', marca: 'Dulce Sol', proveedor: 'Mayorista Norte', qty: '18' },
            ].map((item) => (
              <div key={item.producto} className="grid grid-cols-[2fr_1fr_1.3fr_0.8fr] text-[8px] border-t border-slate-100">
                <div className="px-2 py-1.5">
                  <div className="font-bold text-slate-700">{item.producto}</div>
                  <div className="text-[7px] text-slate-400 uppercase">Minimo configurado</div>
                </div>
                <div className="px-2 py-1.5 text-slate-500">{item.marca}</div>
                <div className="px-2 py-1.5 text-slate-500">{item.proveedor}</div>
                <div className="px-2 py-1.5 text-right font-black text-emerald-600">{item.qty}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (currentDocType === 'lista_precios') {
      const sample = [
        { n: 'Agua mineral 500ml', b: 'Villavicencio', c: '7790315000011', p: '850,00' },
        { n: 'Alfajor triple', b: 'Havanna', c: '7790580000022', p: '1.200,00' },
        { n: 'Gaseosa cola 1.5L', b: 'Coca-Cola', c: '7790895000033', p: '2.450,00' },
      ];
      return (
        <div className="w-full mb-2 text-[9px]">
          {(ticketConfig.showListName || ticketConfig.showListDate) && (
            <div className="flex justify-between font-bold mb-1">
              <span>{ticketConfig.showListName ? 'LISTA: MAYORISTA' : 'LISTA DE PRECIOS'}</span>
              {ticketConfig.showListDate && <span className="font-normal text-slate-500">Fecha: 09/10/2026</span>}
            </div>
          )}
          <div className="border-t border-b border-slate-300 py-0.5 flex font-bold">
            <span className="flex-1">Producto</span>
            {ticketConfig.showBrand && <span className="w-16">Marca</span>}
            {ticketConfig.showBarcode && <span className="w-20">Código</span>}
            <span className="w-14 text-right">Precio</span>
          </div>
          {sample.map((r) => (
            <div key={r.n} className="flex py-0.5 border-b border-slate-100">
              <span className="flex-1 truncate">{r.n}</span>
              {ticketConfig.showBrand && <span className="w-16 truncate">{r.b}</span>}
              {ticketConfig.showBarcode && <span className="w-20 truncate">{r.c}</span>}
              <span className="w-14 text-right">$ {r.p}</span>
            </div>
          ))}
        </div>
      );
    }

    if (currentDocType === 'arqueo') {
      return (
        <div className="w-full space-y-1 mb-2 text-[9px]">
           <div className="flex justify-between border-b border-slate-100 pb-1"><span>Saldo Inicial</span><span>$25,000.00</span></div>
           <div className="flex justify-between text-emerald-600"><span>+ Ventas Efectivo</span><span>$125,400.00</span></div>
           <div className="flex justify-between text-rose-600 border-b border-slate-100 pb-1"><span>- Gastos</span><span>$32,150.00</span></div>
           <div className="flex justify-between font-bold text-[10px] pt-1"><span>Saldo Real</span><span>$113,250.00</span></div>
        </div>
      );
    }

    if (currentDocType === 'recibo' || currentDocType === 'pago') {
      return (
        <div className="w-full space-y-1 mb-2 text-[9px]">
           <div className="border border-slate-200 p-1.5 rounded mb-1 bg-slate-50">
             <div className="font-bold">Recibimos de:</div>
             <div>Cliente Ejemplo SRL</div>
           </div>
           <div className="font-bold border-b pb-0.5 mb-0.5">Concepto</div>
           <div className="flex justify-between"><span>Ref. Op #4422</span><span>$50,000.00</span></div>
           <div className="flex justify-between font-bold border-t pt-1 mt-1"><span>TOTAL</span><span>$50,000.00</span></div>
        </div>
      );
    }

    const isNC = currentDocType === 'nota_credito';
    const items = [
      { name: 'Producto A (21%)', price: isNC ? -1210.00 : 1210.00, taxRate: 21 },
      { name: 'Servicio B (10.5%)', price: isNC ? -110.50 : 110.50, taxRate: 10.5 }
    ];
    
    let totalItems = 0;
    let breakdown = {};

    items.forEach(item => {
      totalItems += item.price;
      const rate = item.taxRate;
      const net = item.price / (1 + (rate / 100));
      const tax = item.price - net;
      if (!breakdown[rate]) breakdown[rate] = { net: 0, tax: 0 };
      breakdown[rate].net += net;
      breakdown[rate].tax += tax;
    });

    const subtotal = totalItems;
    const discountAmount = ticketConfig.showDiscounts ? subtotal * 0.10 : 0; 
    const surchargeAmount = ticketConfig.showDiscounts ? subtotal * 0.05 : 0; 
    const totalFinal = subtotal - discountAmount + surchargeAmount;

    // Ajuste visual de breakdown
    const factorAdjustment = totalFinal / subtotal;
    let totalTax = 0;
    Object.keys(breakdown).forEach(rate => {
        breakdown[rate].tax *= factorAdjustment;
        totalTax += breakdown[rate].tax;
    });

    const isC = ticketConfig.voucherLetter === 'C';

    return (
      <>
        {(currentDocType === 'factura' || currentDocType === 'nota_credito') && (
           <div className="w-full text-center border-y border-slate-800 py-1 mb-2 font-bold text-[10px] bg-slate-100 uppercase flex justify-between px-2 items-center">
              <div className="flex-1 text-left">{currentDocType === 'factura' ? 'FACTURA' : 'NOTA CRÉDITO'}</div>
              <div className="border border-slate-800 px-1.5 bg-white text-sm mx-1 font-black">{ticketConfig.voucherLetter}</div>
              <div className="flex-1 text-right text-[8px] font-normal">N° 0001-00000042</div>
           </div>
        )}

        {ticketConfig.showClientInfo && (
          <div className="w-full border-y border-dashed border-slate-300 py-1 mb-2 text-[9px] leading-tight">
            <div className="flex justify-between"><span>Cliente:</span><span className="font-bold">Juan Pérez</span></div>
            <div className="flex justify-between"><span>CUIT:</span><span>20-12345678-9</span></div>
            <div className="flex justify-between"><span>Cond:</span><span>Cons. Final</span></div>
            <div className="flex justify-between text-slate-500"><span>Dir:</span><span>Av. Siempreviva 742</span></div>
          </div>
        )}
        
        {/* Items */}
        <div className="w-full space-y-1 mb-2">
          {items.map((item, i) => (
            <div key={i} className="flex flex-col text-[9px]">
                <div className="flex justify-between">
                    <span>1x {item.name}</span>
                    <span className={isNC ? 'text-rose-600' : ''}>${fmtMoney2(item.price)}</span>
                </div>
                {ticketConfig.showItemTax && !isC && (
                    <div className="flex justify-end text-[8px] text-slate-400">
                        <span>(Neto: ${fmtMoney2(item.price/1.21)} | IVA: ${fmtMoney2(item.price - item.price/1.21)})</span>
                    </div>
                )}
            </div>
          ))}
        </div>
        
        <div className="w-full border-b border-dashed border-slate-300 my-1"></div>
        
        {/* Totales y Descuentos */}
        <div className="w-full space-y-0.5 mb-2 text-[9px]">
            <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>${fmtMoney2(subtotal)}</span></div>

            {ticketConfig.showDiscounts && (
              <>
                <div className="flex justify-between text-emerald-600"><span>Descuento (10%)</span><span>-${fmtMoney2(discountAmount)}</span></div>
                <div className="flex justify-between text-rose-600"><span>Recargo (5%)</span><span>+${fmtMoney2(surchargeAmount)}</span></div>
              </>
            )}
        </div>

        {ticketConfig.showTaxBreakdown && !isC && (
          <div className="w-full bg-slate-50 p-1.5 rounded mb-2 text-[8px] text-slate-500">
            <div className="font-bold border-b border-slate-200 pb-0.5 mb-0.5">Impuestos</div>
            
            {ticketConfig.showTaxPerRate ? (
                Object.entries(breakdown).map(([rate, vals]) => (
                    <div key={rate} className="flex justify-between"><span>IVA ({rate}%)</span><span>${fmtMoney2(vals.tax)}</span></div>
                ))
            ) : (
                <div className="flex justify-between font-bold"><span>Total IVA</span><span>${fmtMoney2(totalTax)}</span></div>
            )}
          </div>
        )}

        {/* ✅ MÉTODO DE PAGO MOVIDO ARRIBA DE TOTAL */}
        {ticketConfig.showPaymentMethod && (
            <div className="flex justify-between text-[9px] mt-1 mb-1 text-slate-600">
                <span>Método Pago:</span><span className="font-bold">Efectivo</span>
            </div>
        )}

        <div className="w-full border-t border-dashed border-slate-300 my-1"></div>

        {/* Total Final */}
        <div className={`flex justify-between font-bold text-[11px] pt-1 mt-1 ${isNC ? 'text-rose-600' : 'text-slate-900'}`}>
            <span>TOTAL</span><span>${fmtMoney2(totalFinal)}</span>
        </div>

        {ticketConfig.showMonotributoLegend && (
          <div className="w-full border border-slate-300 p-1.5 rounded-lg mt-2 mb-2 text-[7px] text-slate-500 text-justify leading-tight bg-slate-50">
              El crédito fiscal discriminado en el presente comprobante, es computable para el régimen general de impuestos (Ley 27.618).
          </div>
        )}

        {ticketConfig.showQr && (
            <div className="w-full flex justify-between items-center mt-2 border-t border-slate-200 pt-2 mb-2">
                <div className="bg-white p-0.5 border border-slate-100 rounded"><div className="w-10 h-10 bg-slate-900/10 grid place-items-center"><span className="text-[6px] text-slate-400">QR</span></div></div>
                <div className="text-right text-[8px] text-slate-500 leading-tight">
                  <div className="font-bold">CAE: 73456789012345</div>
                  <div>Vto: 10/11/2026</div>
                </div>
            </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-3 font-sans text-slate-700">
      
      {/* 1. HEADER */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black text-slate-900 flex items-center gap-2">
            <Printer className="text-[#51A7FD]" size={24} /> Impresión de Reportes
          </h1>
          <div className="text-xs text-slate-500">Personaliza tus comprobantes y comportamiento.</div>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2 rounded-xl bg-[#0B2A36] text-white font-bold hover:opacity-95 shadow-lg disabled:opacity-50 text-xs uppercase tracking-wider">
          {saving ? <RefreshCcw className="animate-spin" size={16} /> : <Save size={16} />}
          <span>{saving ? 'Guardando...' : 'Guardar'}</span>
        </button>
      </div>

      {/* 3. CONTENIDO PRINCIPAL */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        
        {/* FORMULARIO */}
        <div className="lg:col-span-7 space-y-3">
           {/* GLOBAL SETTINGS FOR POS */}
           <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-4">
              <div className="bg-[#0B2A36] px-4 py-2 flex items-center gap-2">
                 <Settings size={14} className="text-[#3FF2AA]" />
                 <span className="text-[10px] font-black text-white uppercase tracking-widest">Comportamiento Punto de Venta (POS)</span>
              </div>
              <div className="p-4 space-y-3">
                  <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 block">Acción Pre-determinada al Finalizar Venta</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {[
                           { val: 'popout', label: 'Mostrar Modal Interactivo (Actual)', icon: <LayoutTemplate size={14}/> },
                           { val: 'none', label: 'Opción 1: No Imprimir Nada', icon: <Ban size={14}/> },
                           { val: 'ticket', label: 'Opción 2: Imprimir Ticket Interno', icon: <FileText size={14}/> },
                           { val: 'factura', label: 'Opción 3: Auto-Facturar e Imprimir Factura', icon: <CheckCircle2 size={14}/> }
                        ].map(opt => (
                           <button
                             key={opt.val}
                             onClick={() => setPosDefaultAction(opt.val)}
                             className={`flex items-center justify-start gap-2 px-3 py-3 rounded-lg text-[10px] font-black uppercase transition-all border ${
                               posDefaultAction === opt.val
                                 ? 'bg-[#0B2A36] text-white border-[#0B2A36] shadow-md'
                                 : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-white'
                             }`}
                             title={opt.label}
                           >
                             {opt.icon} <span className="truncate">{opt.label}</span>
                           </button>
                        ))}
                    </div>
                    <div className="text-[9px] font-bold text-slate-400 mt-2 ml-1">
                      Si eliges Auto-Facturar, el sistema validará las reglas AFIP fijadas en Config. Facturación. Si no se cumplen, imprimirá un ticket interno de forma automática.
                    </div>
                  </div>

                  {/* Impresión silenciosa: salta el modal de previsualización */}
                  <div className="border-t border-slate-100 pt-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 accent-[#0B2A36] cursor-pointer w-4 h-4"
                        checked={silentPrint}
                        onChange={(e) => setSilentPrint(e.target.checked)}
                      />
                      <div>
                        <div className="text-[10px] font-black text-slate-700 uppercase tracking-widest flex items-center gap-2">
                          <Printer size={12} className="text-[#51A7FD]" />
                          Impresión automática (sin previsualización)
                        </div>
                        <div className="text-[9px] text-slate-400 mt-1 max-w-md leading-tight">
                          Cuando esté activo, los comprobantes se mandan directo a la impresora sin abrir la ventana de previsualización en Impulza, ahorrando un click por venta. Para saltar también el diálogo del navegador, abrí Chrome con el parámetro <code className="bg-slate-100 px-1 rounded text-slate-600 font-mono">--kiosk-printing</code> y configurá tu impresora térmica como predeterminada.
                        </div>
                      </div>
                    </label>
                  </div>
              </div>
           </div>

           <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                 <FileText size={18} className="text-slate-400" /> EDITANDO DOCUMENTO:
              </div>
              <select 
                value={currentDocType}
                onChange={(e) => setCurrentDocType(e.target.value)}
                className="bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-lg p-2 font-bold uppercase outline-none focus:border-[#51A7FD]"
              >
                 {DOC_TYPES.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
           </div>

           <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-[#0B2A36] px-4 py-2 flex items-center gap-2">
                 <Settings size={14} className="text-[#3FF2AA]" />
                 <span className="text-[10px] font-black text-white uppercase tracking-widest">Ajustes Generales</span>
              </div>
              
              <div className="p-4 space-y-3">
                 <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Logo del documento</label>
                    <div className="flex items-center gap-3">
                        {ticketConfig.logoBase64 ? (
                            <div className="relative group w-12 h-12 border rounded-lg overflow-hidden flex items-center justify-center bg-slate-50">
                                <img src={ticketConfig.logoBase64} alt="Logo" className="w-full h-full object-contain" />
                                <button onClick={() => updateCurrentConfig('logoBase64', null)} className="absolute inset-0 bg-black/50 hidden group-hover:flex items-center justify-center text-white"><Trash2 size={16}/></button>
                            </div>
                        ) : (
                            <div className="w-12 h-12 border border-dashed rounded-lg flex items-center justify-center text-slate-300"><Sparkles size={16}/></div>
                        )}
                        <label className="cursor-pointer bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-2 transition-all">
                            <CloudUpload size={14} /> Subir Imagen
                            <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                        </label>
                        <span className="text-[9px] text-slate-400">Max 150KB (JPG/PNG)</span>
                    </div>
                 </div>

                 {/* Comportamiento de Impresión */}
                 {!isPedidoSugerido && !isListaPrecios && (
                 <div className="border-t border-slate-100 pt-3">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 block">Acción al Finalizar Venta</label>
                    <div className="grid grid-cols-3 gap-2">
                        {[
                           { val: 'none', label: 'No Imprimir', icon: <Ban size={14}/> },
                           { val: 'ask', label: 'Preguntar', icon: <MessageSquare size={14}/> },
                           { val: 'always', label: 'Imprimir Siempre', icon: <Printer size={14}/> }
                        ].map(opt => (
                           <button
                             key={opt.val}
                             onClick={() => updateCurrentConfig('printAction', opt.val)}
                             className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-[10px] font-black uppercase transition-all border ${
                               (ticketConfig.printAction || 'ask') === opt.val
                                 ? 'bg-[#0B2A36] text-white border-[#0B2A36] shadow-md'
                                 : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-white'
                             }`}
                           >
                             {opt.icon} {opt.label}
                           </button>
                        ))}
                    </div>
                 </div>
                 )}

                 <div>
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Título</label>
                    <input type="text" className="w-full bg-slate-50 border border-slate-100 rounded-lg p-2 text-xs font-bold text-slate-700 outline-none focus:border-[#51A7FD]"
                      value={ticketConfig.headerText} onChange={(e) => updateCurrentConfig('headerText', e.target.value)} />
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Subtítulo</label>
                      <textarea rows="2" className="w-full bg-slate-50 border border-slate-100 rounded-lg p-2 text-[10px] font-medium text-slate-600 outline-none resize-y"
                        value={ticketConfig.subHeaderText} onChange={(e) => updateCurrentConfig('subHeaderText', e.target.value)} />
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Pie de Página (Default)</label>
                      <textarea rows="2" className="w-full bg-slate-50 border border-slate-100 rounded-lg p-2 text-[10px] font-medium text-slate-600 outline-none resize-y"
                        value={ticketConfig.footerText} onChange={(e) => updateCurrentConfig('footerText', e.target.value)} />
                    </div>
                 </div>

                 {isListaPrecios && (
                   <div>
                     <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Mensaje de WhatsApp</label>
                     <textarea rows="3" className="w-full bg-slate-50 border border-slate-100 rounded-lg p-2 text-[10px] font-medium text-slate-600 outline-none resize-y"
                       value={ticketConfig.whatsappMessage || ''} onChange={(e) => updateCurrentConfig('whatsappMessage', e.target.value)} />
                     <p className="text-[9px] text-slate-400 ml-1 mt-1">
                       Variables: {'{cliente}'} {'{lista}'} {'{empresa}'} {'{fecha}'}. El PDF va adjunto.
                     </p>
                   </div>
                 )}

                 <div className="border-t border-slate-100 pt-3">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 block">Formato Papel</label>
                    <div className="flex bg-slate-50 p-1 rounded-lg border border-slate-100 w-fit">
                        {['58mm','80mm','a4'].map(w => (
                           <button key={w} onClick={() => updateCurrentConfig('printerWidth', w)}
                             className={`px-3 py-1.5 text-[10px] font-black uppercase rounded-md transition-all ${ticketConfig.printerWidth === w ? 'bg-white text-[#0B2A36] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
                             {w}
                           </button>
                        ))}
                    </div>
                 </div>

                 {(currentDocType === 'factura' || currentDocType === 'nota_credito') && (
                    <div className="border-t border-slate-100 pt-3">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 block">Letra (Preview)</label>
                        <div className="flex bg-slate-50 p-1 rounded-lg border border-slate-100 w-fit">
                            {['A','B','C', 'M'].map(l => (
                               <button key={l} onClick={() => updateCurrentConfig('voucherLetter', l)}
                                 className={`px-3 py-1.5 text-[10px] font-black uppercase rounded-md transition-all ${ticketConfig.voucherLetter === l ? 'bg-white text-[#0B2A36] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
                                 {l}
                               </button>
                            ))}
                        </div>
                    </div>
                 )}

                 <div className="border-t border-slate-100 pt-3">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 block">Opciones (Arrastrar para ordenar)</label>
                    <div className="flex flex-col gap-2">
                        {/* RENDERIZADO DINÁMICO SEGÚN fieldsOrder */}
                        {(ticketConfig.fieldsOrder || DEFAULT_DOC_CONFIGS[currentDocType]?.fieldsOrder || DEFAULT_ORDER).map((fieldKey, index) => {
                           const meta = OPTIONS_META.find(m => m.k === fieldKey);
                           if (!meta) return null;
                           
                           return (
                             <div 
                               key={fieldKey}
                               draggable
                               onDragStart={(e) => handleDragStart(e, index)}
                               onDragEnter={(e) => handleDragEnter(e, index)}
                               onDragEnd={handleDragEnd}
                               className="flex items-center gap-3 p-2 rounded-lg border border-slate-100 bg-white hover:border-[#51A7FD] cursor-move transition-all group"
                             >
                                <GripVertical size={14} className="text-slate-300 group-hover:text-[#51A7FD]" />
                                <div className="flex-1 flex items-center justify-between">
                                   <span className="text-[10px] font-bold text-slate-600">{meta.l}</span>
                                   <input 
                                     type="checkbox" 
                                     className="accent-[#0B2A36] cursor-pointer" 
                                     checked={ticketConfig[fieldKey] || false} 
                                     onChange={(e) => updateCurrentConfig(fieldKey, e.target.checked)} 
                                   />
                                </div>
                             </div>
                           );
                        })}
                    </div>
                 </div>
              </div>
           </div>
        </div>

        {/* PREVIEW */}
        <div className="lg:col-span-5">
           <div className="sticky top-4">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col items-center">
                 <div className="w-full bg-[#0B2A36] px-4 py-2 flex items-center gap-2 border-b border-white/10">
                    <Eye size={14} className="text-[#3FF2AA]" />
                    <span className="text-[10px] font-black text-white uppercase tracking-widest">Vista Previa</span>
                 </div>
                 
                 <div className="p-6 w-full bg-slate-200/50 flex justify-center overflow-auto max-h-[calc(100vh-150px)] custom-scrollbar">
                    <div 
                      className={`bg-white shadow-xl border border-slate-200 p-4 font-mono text-xs text-slate-800 flex flex-col transition-all duration-300 relative ${
                        ticketConfig.printerWidth === '58mm' ? 'w-[58mm]' : 
                        ticketConfig.printerWidth === '80mm' ? 'w-[80mm]' : 'w-[210mm] min-h-[297mm]'
                      }`}
                      style={{ 
                        transformOrigin: 'top center',
                        transform: ticketConfig.printerWidth === 'a4' ? 'scale(0.75)' : 'scale(1)',
                        marginBottom: ticketConfig.printerWidth === 'a4' ? '-250px' : '0'
                      }}
                    >
                       {ticketConfig.printerWidth !== 'a4' && (
                         <>
                           <div className="absolute -top-1 left-0 w-full h-2 bg-slate-200/50 skew-y-1"></div>
                           <div className="absolute -bottom-1 left-0 w-full h-2 bg-white skew-y-1 shadow-sm"></div>
                         </>
                       )}
                       
                       <div className="flex flex-col items-center mb-3 text-center">
                         {ticketConfig.showLogo && ticketConfig.logoBase64 ? (
                            <img src={ticketConfig.logoBase64} alt="Logo" className="max-w-[40mm] max-h-[20mm] object-contain mb-2 mix-blend-multiply" />
                         ) : ticketConfig.showLogo && (
                           <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center mb-2"><Sparkles className="text-white" size={14} /></div>
                         )}
                         <div className="font-bold text-[11px] mb-0.5 uppercase leading-tight">{ticketConfig.headerText}</div>
                         <div className="text-[9px] text-slate-500 whitespace-pre-wrap leading-tight">{ticketConfig.subHeaderText}</div>
                       </div>

                       {renderPreviewContent()}
                       
                       <div className="text-center text-[9px] text-slate-500 mt-auto whitespace-pre-wrap pt-3 border-t border-slate-100">
                          {currentDocType === 'presupuesto' ? 'Válido por X días (Dinámico)' : ticketConfig.footerText}
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </div>
      <style>{`.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; }`}</style>
    </div>
  );
}
